import { SystemEntity } from "@erp/shared-types";
import {
  isMatchableInvoiceLineItem,
  type InvoiceLineItemAmounts,
} from "./invoice-line-items";

/**
 * Used only when an org has no `invoiceLineItemPurchaseMatching` config.
 * A saved config — including the AP module default, which requires a
 * purchase-order line — wins over this.
 *
 * Inner arrays are OR; the outer array is AND.
 */
export const FALLBACK_INVOICE_LINE_MATCH_TYPES: readonly (readonly string[])[] =
  [
    [
      SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
      SystemEntity.AR_SALES_ORDER_LINE_ITEM,
      SystemEntity.CHARGE,
    ],
  ];

export function requiredInvoiceLineMatchTypes(
  configured: readonly (readonly string[])[] | null | undefined,
): readonly (readonly string[])[] {
  return configured ?? FALLBACK_INVOICE_LINE_MATCH_TYPES;
}

export const INVOICE_MATCH_GAP_KINDS = [
  "unmatched",
  "needs_confirmation",
  "split_unconfirmed",
  "suggested_split",
] as const;

export type InvoiceMatchGapKind = (typeof INVOICE_MATCH_GAP_KINDS)[number];

export interface InvoiceMatchGap {
  lineItemId: string;
  lineNumber: number;
  description: string | null;
  kind: InvoiceMatchGapKind;
  /** Required groups no confirmed match satisfies. Empty for split gaps. */
  missingGroups: string[][];
}

export interface InvoiceMatchLink {
  targetEntityType: string;
  metadata: Record<string, unknown> | null | undefined;
}

export interface InvoiceMatchLine extends InvoiceLineItemAmounts {
  id: string;
  lineNumber: number;
  description?: string | null;
}

/**
 * Lines that still block acceptance. Informational rows (zero quantity and
 * zero price) are skipped — they are not part of the match.
 *
 * A split group counts only when every part is confirmed. A suggested split
 * (every link still tentative) is its own gap so the dialog can say the
 * suggestion is waiting, rather than that the line has no match at all.
 */
export function invoiceMatchGaps(args: {
  lines: readonly InvoiceMatchLine[];
  linksByLineId: ReadonlyMap<string, readonly InvoiceMatchLink[]>;
  requiredMatchTypes: readonly (readonly string[])[];
  isConfirmed: (metadata: InvoiceMatchLink["metadata"]) => boolean;
}): InvoiceMatchGap[] {
  const gaps: InvoiceMatchGap[] = [];
  for (const line of args.lines) {
    if (!isMatchableInvoiceLineItem(line)) continue;
    const gap = gapForLine(
      line,
      args.linksByLineId.get(line.id) ?? [],
      args.requiredMatchTypes,
      args.isConfirmed,
    );
    if (gap) gaps.push(gap);
  }
  gaps.sort((a, b) => a.lineNumber - b.lineNumber);
  return gaps;
}

/** API error text. The invoice dialog translates the structured gaps itself. */
export function describeInvoiceMatchGaps(
  gaps: readonly InvoiceMatchGap[],
): string {
  const details = gaps.map((gap) => `${lineLabel(gap)} ${gapClause(gap)}`);
  return `Invoice cannot be approved until every line is matched. ${details.join("; ")}.`;
}

function lineLabel(gap: InvoiceMatchGap): string {
  return gap.description
    ? `Line ${gap.lineNumber} (${gap.description})`
    : `Line ${gap.lineNumber}`;
}

function gapClause(gap: InvoiceMatchGap): string {
  switch (gap.kind) {
    case "unmatched":
      return "is not matched";
    case "needs_confirmation":
      return "has a match that still needs to be confirmed";
    case "split_unconfirmed":
      return "is split across charges and not every part is confirmed";
    case "suggested_split":
      return "has a suggested split that still needs to be confirmed";
  }
}

function gapForLine(
  line: InvoiceMatchLine,
  links: readonly InvoiceMatchLink[],
  requiredMatchTypes: readonly (readonly string[])[],
  isConfirmed: (metadata: InvoiceMatchLink["metadata"]) => boolean,
): InvoiceMatchGap | null {
  // Split groups block even when the charge is not a required match type:
  // one confirmed part must not make the line look finished.
  if (hasUnconfirmedSplit(links, isConfirmed)) {
    return splitGap(line, links, isConfirmed);
  }

  const allowed = new Set(requiredMatchTypes.flat());
  const matches = links.filter((link) => allowed.has(link.targetEntityType));
  const missingGroups = requiredMatchTypes.filter(
    (group) =>
      !matches.some(
        (link) =>
          group.includes(link.targetEntityType) && isConfirmed(link.metadata),
      ),
  );
  if (missingGroups.length === 0) return null;

  const everyMissingGroupHasALink = missingGroups.every((group) =>
    matches.some((link) => group.includes(link.targetEntityType)),
  );
  return {
    lineItemId: line.id,
    lineNumber: line.lineNumber,
    description: line.description ?? null,
    kind: everyMissingGroupHasALink ? "needs_confirmation" : "unmatched",
    missingGroups: missingGroups.map((group) => [...group]),
  };
}

function splitGap(
  line: InvoiceMatchLine,
  links: readonly InvoiceMatchLink[],
  isConfirmed: (metadata: InvoiceMatchLink["metadata"]) => boolean,
): InvoiceMatchGap {
  const anyConfirmed = links.some((link) => isConfirmed(link.metadata));
  const onlySplitLinks =
    links.length > 0 &&
    links.every((link) => splitGroupId(link.metadata) !== null);
  return {
    lineItemId: line.id,
    lineNumber: line.lineNumber,
    description: line.description ?? null,
    kind:
      !anyConfirmed && onlySplitLinks ? "suggested_split" : "split_unconfirmed",
    missingGroups: [],
  };
}

function hasUnconfirmedSplit(
  links: readonly InvoiceMatchLink[],
  isConfirmed: (metadata: InvoiceMatchLink["metadata"]) => boolean,
): boolean {
  const confirmedByGroup = new Map<string, boolean>();
  for (const link of links) {
    const groupId = splitGroupId(link.metadata);
    if (groupId === null) continue;
    const confirmed = isConfirmed(link.metadata);
    confirmedByGroup.set(
      groupId,
      (confirmedByGroup.get(groupId) ?? true) && confirmed,
    );
  }
  for (const allConfirmed of confirmedByGroup.values()) {
    if (!allConfirmed) return true;
  }
  return false;
}

function splitGroupId(metadata: InvoiceMatchLink["metadata"]): string | null {
  const groupId = metadata?.splitGroupId;
  return typeof groupId === "string" && groupId.length > 0 ? groupId : null;
}
